// @ts-check
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { fade, makeStyles } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';

import InputBase from '@material-ui/core/InputBase';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { useDebouncedValue } from '../../../../core/utils/hooks/use-debounced-value';

// Cap dropdown options for large fleets — anything beyond this is noise and
// pushing more than this into MUI Autocomplete kills the input. Users can
// type more characters to narrow further.
const MAX_OPTIONS = 50;
// Minimum query length before we even consider populating options. Avoids
// dropping the entire driver list into Autocomplete on focus.
const MIN_QUERY_LENGTH = 2;

const useStyles = makeStyles((theme) => ({
  search: {
    position: 'relative',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: fade(theme.palette.common.black, 0.15),
    '&:hover': {
      backgroundColor: fade(theme.palette.common.black, 0.25)
    },
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginRight: 0,
    marginLeft: 0,
    width: '100%'
  },
  searchIcon: {
    padding: theme.spacing(0, 2),
    height: '100%',
    position: 'absolute',
    pointerEvents: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  inputRoot: {
    color: 'inherit'
  },
  inputInput: {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)}px)`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '40ch'
    }
  }
}));

function matches(driver, needle) {
  const name = (driver.name || '').toLowerCase();
  if (name.includes(needle)) return true;
  const email = (driver.email || '').toLowerCase();
  if (email.includes(needle)) return true;
  const code = (driver.employee_code || '').toLowerCase();
  return code.includes(needle);
}

export function SearchDriversComponent() {
  const classes = useStyles();
  const history = useHistory();

  const [query, setQuery] = useState('');
  // Debounce so we don't recompute the option list on every keystroke for
  // large fleets — the previous version dumped all drivers in unconditionally.
  const debounced = useDebouncedValue(query.trim().toLowerCase(), 200);

  const { drivers } = useSelector((state) => state.drivers);

  const options = useMemo(() => {
    if (debounced.length < MIN_QUERY_LENGTH) return [];
    const out = [];
    for (let i = 0; i < drivers.length && out.length < MAX_OPTIONS; i++) {
      if (matches(drivers[i], debounced)) out.push(drivers[i]);
    }
    return out;
  }, [drivers, debounced]);

  return (
    <div className={classes.search}>
      <div className={classes.searchIcon}>
        <SearchIcon />
      </div>

      <Autocomplete
        id="drivers-search"
        fullWidth={true}
        // Disable MUI's built-in client-side filtering — we do it ourselves
        // against the (already-filtered, capped) options list above.
        filterOptions={(opts) => opts}
        onChange={(event, driver) => {
          // @ts-ignore
          if (driver && driver.id) history.push(`/drivers/${driver.id}/view`);
        }}
        // @ts-ignore
        getOptionSelected={(option, value) => option.id === value.id}
        // @ts-ignore
        getOptionLabel={(option) => option.name || ''}
        options={options}
        noOptionsText={
          debounced.length < MIN_QUERY_LENGTH
            ? `Type at least ${MIN_QUERY_LENGTH} characters to search`
            : 'No matching drivers'
        }
        inputValue={query}
        onInputChange={(_event, value) => setQuery(value)}
        renderInput={(params) => (
          <InputBase
            placeholder="Search drivers…"
            classes={{
              root: classes.inputRoot,
              input: classes.inputInput
            }}
            color="secondary"
            ref={params.InputProps.ref}
            {...params}
          />
        )}
      />
    </div>
  );
}
